/*
 * Repository files of an Expo / React Native app (SPEC decision 29, plan P31): pure model, no React and nothing that
 * touches `window`. Hangar renders what the app's folder needs, already filled for this app, to copy one by one or to
 * download; it never writes to the repository (the source-control token stays read-only).
 *
 * What the files carry is what Hangar's runner and `hangar doctor` expect (packages/cli/src/commands/doctor-expo.ts,
 * apps/worker/src/runner/local-eas-runner.ts): app.json with the project's owner, slug and `extra.eas.projectId`; an
 * eas.json with `"appVersionSource": "local"` and a Node 22 in every profile Hangar's build profiles name (the runner
 * passes that name to the build and injects the HANGAR_* values into it); an app config that reads HANGAR_BUILD_NUMBER
 * and HANGAR_VERSION_NAME; the `eas-build-pre-install` script running `hangar runtime env --out .env`; and an
 * `.easignore` that keeps every `.env*` file out of the upload.
 *
 * "Download all" is one Markdown file, not a patch: app.json and package.json always exist already and Hangar does not
 * know their contents, so a unified diff of them could not apply cleanly; a text with each file's path, whether to merge
 * or add it, and its content never fails halfway. No provider is named in prose (SPEC decision 23): `eas.json` and
 * `.easignore` are file names of the client's repository.
 */
import type { AppFramework, ArtifactKind, Platform } from '@hangar/shared';

/** The Node version of every generated profile: the one the example app builds with on the build service. */
export const NODE_VERSION = '22.23.3';
/** Where the pre-install script takes the Hangar CLI from: the tarball the console downloads (`GET /v1/cli/package`). */
export const CLI_PACKAGE = './vendor/hangar-cli.tgz';
export const PRE_INSTALL_SCRIPT = `npx --yes --package=${CLI_PACKAGE} hangar runtime env --out .env`;
/** The profile eas.json starts with when no build profile names one yet: an installable Android build. */
const FALLBACK_PROFILE: EasProfileSpec = { name: 'apk', android: 'apk' };

export type RepoFileId = 'app.json' | 'app.config.ts' | 'eas.json' | 'package.json' | 'vendor/hangar-cli.tgz' | '.easignore';
/** `merge`: add the keys to the file the app already has; `add`: create the file (or replace it). */
export type RepoFileAction = 'merge' | 'add';

export interface RepoFile {
  id: RepoFileId;
  /** From the repository root: the app's working directory, then the file name. */
  path: string;
  action: RepoFileAction;
  /** The fenced block's language in the download. */
  language: 'json' | 'ts' | 'text';
  /** Null while Hangar cannot fill it yet (app.json before the app's build setup is ready), or for the CLI package. */
  content: string | null;
  note: string;
  /** A binary file the sheet downloads (the Hangar CLI package) instead of showing and copying it. */
  download?: true;
}

export interface RepoFilesProfile {
  slug: string;
  platform: Platform;
  artifactKind: ArtifactKind;
  providerConfig: Record<string, unknown>;
  environments: { providerConfig?: Record<string, unknown> }[];
}

export interface RepoFilesInput {
  app: { slug: string; name: string; workingDirectory: string; framework: AppFramework };
  /** The app.json fragment (`appJsonSnippet` of the app's build setup), or null before it is ready. */
  appJson: string | null;
  profiles: RepoFilesProfile[];
}

export interface EasProfileSpec {
  name: string;
  android?: ArtifactKind;
  ios?: ArtifactKind;
}

/** `apps/expo/` → `apps/expo`; `.`, `./` and `` → the repository root (``). */
export function appFolder(workingDirectory: string): string {
  const trimmed = workingDirectory.trim().replace(/^\.\/+/, '').replace(/\/+$/, '');
  return trimmed === '.' ? '' : trimmed;
}

const pathOf = (folder: string, name: string): string => (folder ? `${folder}/${name}` : name);

/** The eas.json profile names one build profile uses: its own and each environment's, without repeats. */
export function easProfileNames(p: Pick<RepoFilesProfile, 'providerConfig' | 'environments'>): string[] {
  const names = [p.providerConfig.profile, ...p.environments.map((b) => b.providerConfig?.profile)];
  return [...new Set(names.filter((n): n is string => typeof n === 'string' && n.length > 0))];
}

/** One spec per eas.json profile name, in the order the build profiles use them; `unnamed` lists the build profiles that name none. */
export function easProfilesOf(profiles: readonly RepoFilesProfile[]): { specs: EasProfileSpec[]; unnamed: string[] } {
  const specs = new Map<string, EasProfileSpec>();
  const unnamed: string[] = [];
  for (const p of profiles) {
    const names = easProfileNames(p);
    if (!names.length) unnamed.push(p.slug);
    for (const name of names) {
      const spec = specs.get(name) ?? { name };
      spec[p.platform] ??= p.artifactKind;
      specs.set(name, spec);
    }
  }
  return { specs: [...specs.values()], unnamed };
}

const STORE_KINDS: readonly ArtifactKind[] = ['aab', 'ipa'];

/** One eas.json build profile: store distribution only when every artifact is a store one; APK and Simulator builds as asked. */
export function easProfileJson(spec: EasProfileSpec): Record<string, unknown> {
  const kinds = [spec.android, spec.ios].filter((k): k is ArtifactKind => k !== undefined);
  const store = kinds.length > 0 && kinds.every((k) => STORE_KINDS.includes(k));
  return {
    distribution: store ? 'store' : 'internal',
    node: NODE_VERSION,
    ...(spec.android ? { android: { buildType: spec.android === 'apk' ? 'apk' : 'app-bundle' } } : {}),
    ...(spec.ios === 'app_tar_gz' ? { ios: { simulator: true } } : {}),
  };
}

const json = (value: unknown): string => `${JSON.stringify(value, null, 2)}\n`;

export function easJson(specs: readonly EasProfileSpec[]): string {
  const used = specs.length ? specs : [FALLBACK_PROFILE];
  return json({ cli: { version: '>= 16.0.0', appVersionSource: 'local' }, build: Object.fromEntries(used.map((s) => [s.name, easProfileJson(s)])) });
}

/** The app config that applies Hangar's version name and build number (Expo apps). */
export function appConfigTs(app: Pick<RepoFilesInput['app'], 'name' | 'slug'>): string {
  return [
    "import type { ConfigContext, ExpoConfig } from 'expo/config';",
    '',
    '// Hangar sets the version name and the build number of every build (eas.json "appVersionSource": "local").',
    'export default ({ config }: ConfigContext): ExpoConfig => {',
    "  const buildNumber = Number(process.env.HANGAR_BUILD_NUMBER ?? '1');",
    '  return {',
    '    ...config,',
    `    name: config.name ?? ${JSON.stringify(app.name)},`,
    `    slug: config.slug ?? ${JSON.stringify(app.slug)},`,
    "    version: process.env.HANGAR_VERSION_NAME ?? config.version ?? '1.0.0',",
    '    android: { ...config.android, versionCode: buildNumber },',
    '    ios: { ...config.ios, buildNumber: String(buildNumber) },',
    '  };',
    '};',
    '',
  ].join('\n');
}

export function easIgnore(folder: string): string {
  return [
    `# What a build uploads from ${folder || 'the repository root'} (used instead of .gitignore when present).`,
    'node_modules/',
    '.expo/',
    'dist/',
    'web-build/',
    '# Written on the build machine by the eas-build-pre-install script; a local copy is never uploaded.',
    '.env*',
    '',
  ].join('\n');
}

const APP_JSON_NOTE = 'Merge into the expo section of the app’s app.json: the build checks the owner, the slug and the project id. Hangar already created this project: do not create another one.';
const PACKAGE_JSON_NOTE = 'Merge into scripts: before installing, the build machine downloads this build’s environment variables into .env with the Hangar CLI in vendor/hangar-cli.tgz (next file).';
const CLI_NOTE = 'The Hangar CLI the pre-install script runs (no dependencies). Download it from Repository files in Hangar and commit it at this path.';
const APP_JSON_PENDING = 'Not ready yet: Hangar fills this file once the app’s build setup is ready (Build setup on the app’s page).';

function easJsonNote(unnamed: readonly string[], framework: AppFramework): string {
  const parts = ['The build configuration file: one profile per name your build profiles use, each with Node 22, and "appVersionSource": "local" so Hangar’s build numbers win. If the file exists, add these profiles and the cli lines.'];
  if (framework === 'react_native') parts.push('In a React Native app without Expo, the native projects set the version: have them read HANGAR_BUILD_NUMBER and HANGAR_VERSION_NAME.');
  if (unnamed.length) parts.push(`${unnamed.join(', ')} ${unnamed.length === 1 ? 'names' : 'name'} no profile in eas.json yet: name one in the build profile’s “Profile in the build configuration file” so Hangar can hand the build its variables.`);
  return parts.join(' ');
}

/** Every file the app's folder needs, in the order to read them; app.config.ts only for Expo apps. */
export function repoFiles(input: RepoFilesInput): RepoFile[] {
  const folder = appFolder(input.app.workingDirectory);
  const { specs, unnamed } = easProfilesOf(input.profiles);
  const expo = input.app.framework === 'expo';
  const files: RepoFile[] = [
    { id: 'app.json', path: pathOf(folder, 'app.json'), action: 'merge', language: 'json', content: input.appJson, note: input.appJson ? APP_JSON_NOTE : APP_JSON_PENDING },
    { id: 'eas.json', path: pathOf(folder, 'eas.json'), action: 'add', language: 'json', content: easJson(specs), note: easJsonNote(unnamed, input.app.framework) },
    { id: 'package.json', path: pathOf(folder, 'package.json'), action: 'merge', language: 'json', content: json({ scripts: { 'eas-build-pre-install': PRE_INSTALL_SCRIPT } }), note: PACKAGE_JSON_NOTE },
    { id: 'vendor/hangar-cli.tgz', path: pathOf(folder, CLI_PACKAGE.slice(2)), action: 'add', language: 'text', content: null, note: CLI_NOTE, download: true },
    { id: '.easignore', path: pathOf(folder, '.easignore'), action: 'add', language: 'text', content: easIgnore(folder), note: 'What a build uploads from the app’s folder: local .env files never leave your machine.' },
  ];
  if (expo) files.splice(1, 0, { id: 'app.config.ts', path: pathOf(folder, 'app.config.ts'), action: 'add', language: 'ts', content: appConfigTs({ name: input.app.name, slug: input.app.slug }), note: 'Applies Hangar’s version name and build number. If the app already has an app.config, add the version, versionCode and buildNumber lines to it.' });
  return files;
}

/** The file names an app's folder needs, in reading order (what `repoFiles` fills): app.config.ts only for Expo apps. */
export const repoFileNames = (framework: AppFramework): RepoFileId[] =>
  framework === 'expo' ? ['app.json', 'app.config.ts', 'eas.json', 'package.json', 'vendor/hangar-cli.tgz', '.easignore'] : ['app.json', 'eas.json', 'package.json', 'vendor/hangar-cli.tgz', '.easignore'];

/** A stable, testid-safe key of a file: `app.json` → `app-json`, `.easignore` → `easignore`. */
export const fileKey = (id: RepoFileId): string => id.replace(/^\./, '').replace(/[^a-z0-9]+/g, '-');

export const bundleFilename = (appSlug: string): string => `hangar-files-${appSlug}.md`;

/** "Download all": one Markdown text with every file's path, action, note and content. */
export function bundleText(app: Pick<RepoFilesInput['app'], 'name' | 'slug' | 'workingDirectory'>, files: readonly RepoFile[]): string {
  const folder = appFolder(app.workingDirectory);
  const head = [
    `# Repository files for ${app.name}`,
    '',
    `Hangar filled these files for the app \`${app.slug}\` (${folder ? `folder \`${folder}\`` : 'the repository root'}). Hangar never writes to your repository: add them, commit and push, then run a build.`,
  ];
  const sections = files.flatMap((f) => [
    '',
    `## ${f.path} (${f.action === 'merge' ? 'merge into the existing file' : 'add'})`,
    '',
    f.note,
    ...(f.content === null ? [] : ['', `\`\`\`${f.language}`, f.content.replace(/\n$/, ''), '```']),
  ]);
  return `${[...head, ...sections].join('\n')}\n`;
}
